import { useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Box, CircularProgress } from "@mui/material";
import AppLayout from "./components/AppLayout";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PlayerPage from "./pages/PlayerPage";
import MosaicPage from "./pages/MosaicPage";
import ConfigPage from "./pages/ConfigPage";
import PlatformsPage from "./pages/PlatformsPage";
import AnnotationsPage from "./pages/AnnotationsPage";
import StreamsPage from "./pages/StreamsPage";
import UserGroupsPage from "./pages/UserGroupsPage";
import UsersPage from "./pages/UsersPage";
import ForcePasswordResetPage from "./pages/ForcePasswordResetPage";
import LogoutPage from "./pages/LogoutPage";
import WhepDebugPage from "./pages/WhepDebugPage";
import LogPage from "./pages/LogPage";
import ClientPreferencesPage from "./pages/ClientPreferencesPage";
import SystemMonitorPage from "./pages/SystemMonitorPage";
import RecordingsPage from "./pages/RecordingsPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppProvider, useAppContext } from "./context/AppContext";
import RequireAdmin from "./components/RequireAdmin";
import RequireAuth from "./components/RequireAuth";
import { ThemePreferenceProvider } from "./context/ThemePreferenceContext";
import { StreamsStateProvider } from "./context/StreamsStateContext";
import { StreamOutputsStateProvider } from "./context/StreamOutputsStateContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnReconnect: false
    }
  }
});

const LAST_ROUTE_STORAGE_KEY = "skyview.navigation.lastRoute";
const TRANSIENT_ROUTE_QUERY_PARAMS = ["_sv_boot", "_sv_no_sw"];
const RESTORABLE_ROUTE_PREFIXES = [
  "/player",
  "/mosaic",
  "/platforms",
  "/annotations",
  "/streams",
  "/users",
  "/usergroups",
  "/logs",
  "/system-monitor",
  "/recordings",
  "/config",
  "/preferences",
  "/debug/whep",
  "/login",
  "/register",
  "/password-reset"
];

const normalizePathnameForRouting = (pathname: string): string => {
  const trimmed = pathname.trim();
  if (!trimmed) {
    return "/";
  }

  const hostPrefixMatch = trimmed.match(
    /^\/([^/]+:[0-9]+|[^/]+\.[^/]+)(?:\/|$)/u
  );

  if (!hostPrefixMatch) {
    return trimmed;
  }

  const prefix = `/${hostPrefixMatch[1]}`;
  const remainder = trimmed.slice(prefix.length);
  if (!remainder || remainder === "/") {
    return "/";
  }
  return remainder.startsWith("/") ? remainder : `/${remainder}`;
};

const normalizeRouteSearch = (search: string): string => {
  const source = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(source);
  TRANSIENT_ROUTE_QUERY_PARAMS.forEach((param) => {
    params.delete(param);
  });
  const normalized = params.toString();
  return normalized ? `?${normalized}` : "";
};

const isRouteRestorable = (pathname: string): boolean => {
  return RESTORABLE_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const normalizeRestorableRoute = (value: string | null): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed, "http://localhost");
    const pathname = normalizePathnameForRouting(parsed.pathname || "/");
    const search = normalizeRouteSearch(parsed.search);
    if (pathname === "/" || pathname === "/logout" || !isRouteRestorable(pathname)) {
      return null;
    }

    return `${pathname}${search}${parsed.hash}`;
  } catch {
    return null;
  }
};

const readLastRoute = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return normalizeRestorableRoute(window.localStorage.getItem(LAST_ROUTE_STORAGE_KEY));
  } catch {
    return null;
  }
};

const LastRouteTracker = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const startupTargetRouteRef = useRef<string | null>(null);
  const restoreAttemptedRef = useRef(false);
  const [restoreResolved, setRestoreResolved] = useState(false);

  useEffect(() => {
    if (restoreAttemptedRef.current) {
      return;
    }
    restoreAttemptedRef.current = true;

    const restoreOnStartup = () => {
      const route = readLastRoute();
      const startupDelayMs = 200;
      const timer = window.setTimeout(() => {
        const normalizedCurrentPath = normalizePathnameForRouting(window.location.pathname || "/");
        const normalizedCurrentSearch = normalizeRouteSearch(window.location.search);
        const canReplaceCurrent =
          normalizedCurrentPath === "/" ||
          (normalizedCurrentPath === "/player" && normalizedCurrentSearch === "");
        const currentRoute = normalizeRestorableRoute(
          `${window.location.pathname}${window.location.search}${window.location.hash}`
        );

        if (route && canReplaceCurrent && currentRoute !== route) {
          startupTargetRouteRef.current = route;
          navigate(route, { replace: true });
        } else {
          startupTargetRouteRef.current = null;
        }

        setRestoreResolved(true);
      }, startupDelayMs);

      return () => {
        window.clearTimeout(timer);
      };
    };

    return restoreOnStartup();
  }, [navigate]);

  useEffect(() => {
    if (!restoreResolved) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const route = normalizeRestorableRoute(`${location.pathname}${location.search}${location.hash}`);
    if (!route) {
      return;
    }

    const startupTargetRoute = startupTargetRouteRef.current;
    if (startupTargetRoute && route !== startupTargetRoute) {
      return;
    }
    if (startupTargetRoute && route === startupTargetRoute) {
      startupTargetRouteRef.current = null;
    }

    try {
      window.localStorage.setItem(LAST_ROUTE_STORAGE_KEY, route);
    } catch {
      // Ignore storage failures (private mode, quota, etc.)
    }
  }, [location.pathname, location.search, location.hash, restoreResolved]);

  return null;
};

const InitialRedirect = () => {
  const { user, authorizedFetch, userManagementEnabled, userManagementResolved } = useAuth();
  const { serverConfig, isDemoMode } = useAppContext();
  const restoredRoute = useMemo(() => readLastRoute(), []);

  const restoredPathname = useMemo(() => {
    if (!restoredRoute) {
      return null;
    }

    try {
      return new URL(restoredRoute, "http://localhost").pathname;
    } catch {
      return null;
    }
  }, [restoredRoute]);

  const isUserManagementEnabled =
    userManagementEnabled && serverConfig.userManagementEnabled;
  const isLoggedIn = Boolean(user);
  const hasAdminRights = !isUserManagementEnabled || (user?.role ?? "").trim().toLowerCase() === "admin";
  const shouldFetchStreams = !isUserManagementEnabled || Boolean(user);

  const {
    data: streams,
    isLoading: isStreamsLoading,
    isError: isStreamsError
  } = useQuery<unknown[]>({
    queryKey: ["streams"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/streams");
      const payload = (await response.json().catch(() => null)) as {
        streams?: unknown;
        error?: string;
      } | null;

      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load streams");
      }

      return Array.isArray(payload?.streams) ? payload.streams : [];
    },
    enabled: shouldFetchStreams && !restoredRoute
  });

  if (!userManagementResolved) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isUserManagementEnabled && !user) {
    if ((restoredPathname === "/login" || restoredPathname === "/register") && restoredRoute) {
      return <Navigate to={restoredRoute} replace />;
    }
    return (
      <Navigate
        to="/login"
        state={restoredRoute ? { from: restoredRoute } : undefined}
        replace
      />
    );
  }

  if (user?.forcePasswordReset) {
    return <Navigate to="/password-reset" replace />;
  }

  if (restoredRoute) {
    return <Navigate to={restoredRoute} replace />;
  }

  if (shouldFetchStreams) {
    if (isStreamsLoading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!isStreamsError && Array.isArray(streams) && streams.length === 0 && isLoggedIn && (hasAdminRights || isDemoMode)) {
      return <Navigate to="/streams" replace />;
    }
  }

  return <Navigate to="/player" replace />;
};

const MosaicRoute = () => {
  const { serverConfig } = useAppContext();

  if (!serverConfig.enablePreview) {
    return <Navigate to="/player" replace />;
  }

  return (
    <RequireAuth>
      <MosaicPage />
    </RequireAuth>
  );
};

const RecordingsRoute = () => {
  const { serverConfig, iceServersLoading } = useAppContext();

  if (iceServersLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!serverConfig.recastLicensed || !serverConfig.recordLicensed) {
    return <Navigate to="/streams" replace />;
  }

  return (
    <RequireAdmin>
      <RecordingsPage />
    </RequireAdmin>
  );
};

const App = () => {
  return (
    <ThemePreferenceProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppProvider>
            <StreamsStateProvider>
              <StreamOutputsStateProvider>
                <BrowserRouter>
                  <LastRouteTracker />
                  <AppLayout>
                    <Routes>
                      <Route path="/" element={<InitialRedirect />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route
                        path="/password-reset"
                        element={
                          <RequireAuth>
                            <ForcePasswordResetPage />
                          </RequireAuth>
                        }
                      />
                      <Route path="/player" element={<PlayerPage />} />
                      <Route
                        path="/mosaic"
                        element={<MosaicRoute />}
                      />
                      <Route
                        path="/platforms"
                        element={
                          <RequireAuth>
                            <PlatformsPage />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/annotations"
                        element={
                          <RequireAuth>
                            <AnnotationsPage />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/streams"
                        element={
                          <RequireAuth>
                            <StreamsPage />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/users"
                        element={
                          <RequireAdmin>
                            <UsersPage />
                          </RequireAdmin>
                        }
                      />
                      <Route
                        path="/usergroups"
                        element={
                          <RequireAdmin>
                            <UserGroupsPage />
                          </RequireAdmin>
                        }
                      />
                      <Route
                        path="/logs"
                        element={
                          <RequireAdmin>
                            <LogPage />
                          </RequireAdmin>
                        }
                      />
                      <Route
                        path="/system-monitor"
                        element={
                          <RequireAdmin>
                            <SystemMonitorPage />
                          </RequireAdmin>
                        }
                      />
                      <Route path="/recordings" element={<RecordingsRoute />} />
                      <Route path="/config" element={<ConfigPage />} />
                      <Route path="/preferences" element={<ClientPreferencesPage />} />
                      <Route path="/debug/whep" element={<WhepDebugPage />} />
                      <Route path="/logout" element={<LogoutPage />} />
                      <Route path="*" element={<InitialRedirect />} />
                    </Routes>
                  </AppLayout>
                </BrowserRouter>
              </StreamOutputsStateProvider>
            </StreamsStateProvider>
          </AppProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemePreferenceProvider>
  );
};

export default App;
